import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { images } from "@/i18n/translations";
import { Reveal } from "@/components/site/Reveal";

const ExperienceCard = ({ item, image, reverse, index }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <div
      ref={ref}
      data-testid={`experience-card-${item.no}`}
      className={`relative grid grid-cols-1 items-center gap-8 md:gap-14 lg:grid-cols-2 ${
        reverse ? "lg:[&>*:first-child]:order-2" : ""
      }`}
    >
      {/* Image */}
      <Reveal
        y={60}
        className={`relative ${reverse ? "lg:ml-auto" : ""}`}
      >
        <div className="relative aspect-[4/5] w-full overflow-hidden bg-sand md:aspect-[5/6] lg:max-w-[520px]">
          <motion.img
            style={{ y: imgY, scale: 1.12 }}
            src={image}
            alt={item.title}
            className="h-full w-full object-cover will-change-transform"
          />
          <div className="absolute inset-0 ring-1 ring-inset ring-ink/10" />
          <span className="absolute left-0 top-0 bg-bone px-4 py-2 font-mono text-[11px] uppercase tracking-[0.25em] text-olive">
            Nr. {item.no}
          </span>
        </div>
      </Reveal>

      {/* Text */}
      <div className={`${reverse ? "lg:pr-10" : "lg:pl-10"}`}>
        <Reveal delay={0.1}>
          <span className="font-display text-6xl font-medium leading-none text-hairline md:text-8xl">
            {item.no}
          </span>
          <h3 className="mt-4 font-display text-3xl font-medium uppercase leading-none tracking-tight text-ink md:text-5xl">
            {item.title}
          </h3>
          <p className="mt-5 max-w-md font-body text-base leading-relaxed text-stone">
            {item.desc}
          </p>

          <dl className="mt-8 grid max-w-md grid-cols-3 border-t border-hairline">
            {item.meta.map((mrow) => (
              <div key={mrow.k} className="border-r border-hairline py-4 pr-3 last:border-r-0">
                <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone/60">
                  {mrow.k}
                </dt>
                <dd className="mt-1.5 font-body text-sm font-semibold text-ink">
                  {mrow.v}
                </dd>
              </div>
            ))}
          </dl>

          <a
            href="#contact"
            data-testid={`experience-cta-${item.no}`}
            className="group mt-8 inline-flex items-center gap-3 font-body text-[12px] font-semibold uppercase tracking-[0.2em] text-ink"
          >
            <span className="relative">
              {item.cta}
              <span className="absolute -bottom-1 left-0 h-px w-full origin-left scale-x-100 bg-ink transition-transform duration-500 group-hover:scale-x-0" />
            </span>
            <span className="flex h-9 w-9 items-center justify-center rounded-full border border-ink transition-colors duration-500 group-hover:bg-ink group-hover:text-bone">
              →
            </span>
          </a>
        </Reveal>
      </div>
    </div>
  );
};

export const ExperienceJourney = () => {
  const { t } = useLang();
  const e = t.experiences;
  const cardImages = [images.card1, images.card2, images.card3];

  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start 0.6", "end 0.9"],
  });
  const pathLength = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section
      id="experiences"
      data-testid="experiences-section"
      className="relative overflow-hidden bg-bone py-24 md:py-36"
    >
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <Reveal className="mb-20 flex flex-col gap-4 md:mb-28 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-olive">
              {e.overline}
            </span>
            <h2 className="mt-4 font-display text-5xl font-medium uppercase leading-[0.9] tracking-tight text-ink md:text-7xl">
              {e.title}
            </h2>
          </div>
          <span className="font-script text-3xl text-rust md:text-4xl">
            {t.hero.script}
          </span>
        </Reveal>

        {/* Journey */}
        <div ref={sectionRef} className="relative">
          {/* Winding animated path (desktop) */}
          <svg
            className="pointer-events-none absolute left-1/2 top-0 hidden h-full w-[70%] -translate-x-1/2 lg:block"
            viewBox="0 0 1000 2600"
            preserveAspectRatio="none"
            fill="none"
            aria-hidden
          >
            <motion.path
              d="M 500 0 C 500 260 120 360 120 620 C 120 920 900 980 900 1300 C 900 1640 140 1680 140 2000 C 140 2280 520 2360 520 2600"
              stroke="#8C4A32"
              strokeWidth="1.5"
              strokeDasharray="2 8"
              vectorEffect="non-scaling-stroke"
              style={{ pathLength }}
            />
          </svg>

          <div className="flex flex-col gap-28 md:gap-48">
            {e.items.map((item, i) => (
              <ExperienceCard
                key={item.no}
                item={item}
                image={cardImages[i]}
                reverse={i % 2 === 1}
                index={i}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
